import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Skeleton } from '@material-ui/lab';
import { FormattedMessage } from 'react-intl';
import { DateTime } from 'luxon';
import { mapIsoStringtoUtcObject } from '~/modules/common/dates/convert';
import { getTotalAllocatedHoursStartingFromDate } from '~/modules/resourcing/util';

const useStyles = makeStyles(theme => ({
  alert: {
    padding: theme.spacing(1, 1, 1, 0)
  },
  loading: {
    marginRight: theme.spacing(1)
  },
  bulletList: {
    padding: theme.spacing(0, 2)
  },
  resultHeader: { fontWeight: 500 }
}));

const boldText = chunks => <strong>{chunks}</strong>;

const getDisplayTitle = ({ userDisplayText, releaseDate }) => ({
  userDisplayText,
  releaseDate: releaseDate
    ? mapIsoStringtoUtcObject(releaseDate).toLocaleString(DateTime.DATE_MED)
    : null,
  b: boldText
});

const getFormattedDisplayCount = ({ taskUris }) => ({
  taskCount: taskUris.length,
  b: boldText
});

const getFormattedDisplayHoursRemaining = ({ hours }) => ({
  allocatedHours: hours.toFixed(2),
  b: boldText
});

const ImpactedTaskSummaryBanner = ({
  isLoading,
  userAssignedImpactedTaskSummary,
  releaseDate,
  resourceAllocation,
  userDisplayText
}) => {
  const classes = useStyles();

  if (isLoading) return <Skeleton height={50} className={classes.loading} />;

  const resourceAllocationHours = resourceAllocation
    ? getTotalAllocatedHoursStartingFromDate({
        date: releaseDate ? releaseDate.plus({ days: 1 }) : null,
        allocation: resourceAllocation
      })
    : null;

  const taskUris =
    userAssignedImpactedTaskSummary?.impactedTaskSummary.taskUris;
  const taskAllocationHours =
    userAssignedImpactedTaskSummary?.impactedTaskSummary
      .totalHoursRemainingAsOfDate;

  const hasTaskAssignment = Boolean(taskUris?.length);

  return hasTaskAssignment || resourceAllocationHours ? (
    <div className={classes.alert}>
      <Alert severity="warning" icon={false}>
        <>
          <Typography
            variant="subtitle1"
            gutterBottom
            className={classes.resultHeader}
          >
            <FormattedMessage
              id={
                releaseDate
                  ? 'releaseResourceRequestDialog.impactedTaskSummaryWithAsOfDate'
                  : 'releaseResourceRequestDialog.impactedTaskSummaryWithOutAsOfDate'
              }
              values={getDisplayTitle({
                userDisplayText:
                  userAssignedImpactedTaskSummary?.displayText ||
                  userDisplayText,
                releaseDate
              })}
            />
          </Typography>

          <ul className={classes.bulletList}>
            {resourceAllocationHours > 0 && (
              <li>
                <FormattedMessage
                  id="releaseResourceRequestDialog.resourceAllocationHours"
                  values={getFormattedDisplayHoursRemaining({
                    hours: resourceAllocationHours
                  })}
                />
              </li>
            )}
            {hasTaskAssignment && (
              <>
                {taskAllocationHours > 0 &&
                  taskAllocationHours !== resourceAllocationHours && (
                    <li>
                      <FormattedMessage
                        id="releaseResourceRequestDialog.impactedTaskSummaryAllocationRemaining"
                        values={getFormattedDisplayHoursRemaining({
                          hours: taskAllocationHours
                        })}
                      />
                    </li>
                  )}
                <li>
                  <FormattedMessage
                    id="releaseResourceRequestDialog.impactedTaskSummaryAssignedTasks"
                    values={getFormattedDisplayCount({ taskUris })}
                  />
                </li>
              </>
            )}
          </ul>
        </>
      </Alert>
    </div>
  ) : null;
};

ImpactedTaskSummaryBanner.propTypes = {
  isLoading: PropTypes.bool,
  userAssignedImpactedTaskSummary: PropTypes.object,
  releaseDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  resourceAllocation: PropTypes.object,
  userDisplayText: PropTypes.string
};

export default ImpactedTaskSummaryBanner;
